import { Box, Text, Flex } from '@chakra-ui/react';
import * as _ from 'lodash';
import React from 'react';

import { CreditAgreement } from '@finley-technologies/finley-types';

import {
  AssignmentSummaryAccordion,
  AssignmentSummaryAccordionRow,
  StatRow,
} from './SharedComponents';
import type { PortfolioTestStats } from './TestStats';
import {
  borrowingBaseSummaryFormatterMap,
  getNumberedSubRowLabel,
} from 'packages/borrowing-base/ui/borrowingBaseSummary/utils';
import { colors } from 'packages/common-frontend/colors';

type EligibilityStatsProps = {
  initialStats: PortfolioTestStats;
  proposedStats: PortfolioTestStats;
  parsedCreditAgreements: CreditAgreement.DomainEntity.CreditAgreementFullArtifact[];
  creditFacilitySlugMap: Map<string, string>;
};
export const EligibilityStats = ({
  initialStats,
  proposedStats,
  parsedCreditAgreements,
  creditFacilitySlugMap,
}: EligibilityStatsProps) => {
  return (
    <Box mt="24px">
      <Flex
        borderBottom="1px solid"
        borderColor={colors.gray[200]}
        p="8px 0px 8px"
      >
        <Text fontWeight="bold">Eligibility</Text>
      </Flex>
      <AssignmentSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <StatRow
            label="Total Eligibility Kickout"
            calcTerminologiesLabel="Total Eligibility Kickout"
            initial={
              <Text fontWeight="medium" textAlign="justify">
                {borrowingBaseSummaryFormatterMap.currency(
                  initialStats.aggregateEligibilityKickouts,
                )}
              </Text>
            }
            proposed={
              <Text fontWeight="medium">
                {borrowingBaseSummaryFormatterMap.currency(
                  proposedStats.aggregateEligibilityKickouts,
                )}
              </Text>
            }
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
          />
        )}
        accordionPanel={
          <Box mt="24px">
            {_.map(parsedCreditAgreements, (creditAgreement, index) => {
              const {
                borrowingBaseMechanics: {
                  calcAgentMechanics: { loanProductMechanics },
                },
                creditFacilityId,
              } = creditAgreement;
              return (
                <Box
                  mt={index > 0 ? '16px' : '0px'}
                  key={`eligibility-kickout-${creditFacilityId}`}
                >
                  <AssignmentSummaryAccordion
                    accordionButton={({ hasAccordionPanel, isExpanded }) => (
                      <AssignmentSummaryAccordionRow
                        label={creditFacilitySlugMap.get(creditFacilityId)}
                        initialValue={
                          initialStats.creditFacilityTestStats[creditFacilityId]
                            .totalEligibilityKickout
                        }
                        proposedValue={
                          proposedStats.creditFacilityTestStats[
                            creditFacilityId
                          ].totalEligibilityKickout
                        }
                        valueType="currency"
                        hasAccordionPanel={hasAccordionPanel}
                        isExpanded={isExpanded}
                        fullRowBackground
                        background="medium"
                        level={2}
                      />
                    )}
                    accordionPanel={
                      <Box mt="16px">
                        {_.map(loanProductMechanics, (loanProduct) => {
                          let eligibilityTestIndex = -1;
                          return (
                            <Box
                              mt="8px"
                              key={`eligibility-kickout-${loanProduct.entity.id}`}
                            >
                              <AssignmentSummaryAccordion
                                accordionButton={({
                                  hasAccordionPanel,
                                  isExpanded,
                                }) => (
                                  <AssignmentSummaryAccordionRow
                                    label={loanProduct.entity.name}
                                    initialValue={
                                      initialStats.creditFacilityTestStats[
                                        creditFacilityId
                                      ].eligibilityKickoutByLoanProductId[
                                        loanProduct.entity.id
                                      ]
                                    }
                                    proposedValue={
                                      proposedStats.creditFacilityTestStats[
                                        creditFacilityId
                                      ].eligibilityKickoutByLoanProductId[
                                        loanProduct.entity.id
                                      ]
                                    }
                                    valueType="currency"
                                    hasAccordionPanel={hasAccordionPanel}
                                    isExpanded={isExpanded}
                                    background="light"
                                    level={3}
                                  />
                                )}
                                accordionPanel={
                                  <Box mt="16px">
                                    {_.map(
                                      loanProduct.eligibilityTests,
                                      (eligibilityTest) => {
                                        if (
                                          eligibilityTest._config_
                                            .eligibilityTestConfig
                                        ) {
                                          eligibilityTestIndex += 1;
                                          const eligibilityTestConfig =
                                            eligibilityTest._config_
                                              .eligibilityTestConfig;
                                          const initialEligibilityReduction =
                                            initialStats
                                              .creditFacilityTestStats[
                                              creditFacilityId
                                            ].eligibilityTestsByLoanProductId[
                                              loanProduct.entity.id
                                            ][
                                              eligibilityTestConfig.propertyKey
                                            ];
                                          const proposedEligibilityReduction =
                                            proposedStats
                                              .creditFacilityTestStats[
                                              creditFacilityId
                                            ].eligibilityTestsByLoanProductId[
                                              loanProduct.entity.id
                                            ][
                                              eligibilityTestConfig.propertyKey
                                            ];
                                          return (
                                            <Box
                                              mt="16px"
                                              key={`loan-product-eligibility-test-${eligibilityTestConfig.propertyKey}`}
                                            >
                                              <AssignmentSummaryAccordionRow
                                                subLabel={getNumberedSubRowLabel(
                                                  eligibilityTestIndex,
                                                  eligibilityTestConfig.propertyKey,
                                                )}
                                                initialValue={
                                                  initialEligibilityReduction
                                                }
                                                proposedValue={
                                                  proposedEligibilityReduction
                                                }
                                                valueType="currency"
                                                isSecondLevelAccordion
                                                background="light"
                                                level={4}
                                              />
                                            </Box>
                                          );
                                        }
                                        return (
                                          <Box
                                            key={`loan-product-eligibility-test-${eligibilityTest.value}`}
                                          />
                                        );
                                      },
                                    )}
                                  </Box>
                                }
                                defaultIsExpanded={
                                  loanProductMechanics.length === 1
                                }
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    }
                    defaultIsExpanded={parsedCreditAgreements.length === 1}
                  />
                </Box>
              );
            })}
          </Box>
        }
      />
      <AssignmentSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <StatRow
            label="Total Eligible Exposure"
            calcTerminologiesLabel="Total Eligible Exposure"
            initial={
              <Text fontWeight="medium" textAlign="justify">
                {borrowingBaseSummaryFormatterMap.currency(
                  initialStats.aggregateExposureLessEligibilityKickouts,
                )}
              </Text>
            }
            proposed={
              <Text fontWeight="medium">
                {borrowingBaseSummaryFormatterMap.currency(
                  proposedStats.aggregateExposureLessEligibilityKickouts,
                )}
              </Text>
            }
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
          />
        )}
        accordionPanel={
          <Box mt="24px">
            {_.map(parsedCreditAgreements, (creditAgreement, index) => {
              const {
                borrowingBaseMechanics: {
                  calcAgentMechanics: { loanProductMechanics },
                },
                creditFacilityId,
              } = creditAgreement;
              return (
                <Box
                  mt={index > 0 ? '16px' : '0px'}
                  key={`credit-facility-eligible-exposure-${creditFacilityId}`}
                >
                  <AssignmentSummaryAccordion
                    accordionButton={({ hasAccordionPanel, isExpanded }) => (
                      <AssignmentSummaryAccordionRow
                        label={creditFacilitySlugMap.get(creditFacilityId)}
                        initialValue={
                          initialStats.creditFacilityTestStats[creditFacilityId]
                            .totalExposureLessEligibilityKickouts
                        }
                        proposedValue={
                          proposedStats.creditFacilityTestStats[
                            creditFacilityId
                          ].totalExposureLessEligibilityKickouts
                        }
                        valueType="currency"
                        hasAccordionPanel={hasAccordionPanel}
                        isExpanded={isExpanded}
                        fullRowBackground
                        background="medium"
                        level={2}
                      />
                    )}
                    accordionPanel={
                      <Box mt="16px">
                        {_.map(loanProductMechanics, (loanProduct) => {
                          return (
                            <Box
                              mt="8px"
                              key={`loan-product-eligibile-exposure-${loanProduct.entity.id}`}
                            >
                              <AssignmentSummaryAccordion
                                accordionButton={({
                                  hasAccordionPanel,
                                  isExpanded,
                                }) => (
                                  <AssignmentSummaryAccordionRow
                                    label={loanProduct.entity.name}
                                    initialValue={
                                      initialStats.creditFacilityTestStats[
                                        creditFacilityId
                                      ]
                                        .totalExposureLessEligibilityKickoutsByLoanProductId[
                                        loanProduct.entity.id
                                      ]
                                    }
                                    proposedValue={
                                      proposedStats.creditFacilityTestStats[
                                        creditFacilityId
                                      ]
                                        .totalExposureLessEligibilityKickoutsByLoanProductId[
                                        loanProduct.entity.id
                                      ]
                                    }
                                    valueType="currency"
                                    hasAccordionPanel={hasAccordionPanel}
                                    isExpanded={isExpanded}
                                    background="light"
                                    level={3}
                                  />
                                )}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    }
                    defaultIsExpanded={parsedCreditAgreements.length === 1}
                  />
                </Box>
              );
            })}
          </Box>
        }
      />
    </Box>
  );
};
